import React from 'react';

import {
  Tag as ChakraTag,
  TagProps as ChakraTagProps,
  HStack,
  TagCloseButton,
  TagLabel,
  TagLeftIcon,
  TagRightIcon,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';

export interface TagProps extends ChakraTagProps {
  children: React.ReactNode;
  onClose?: () => void;
  onClick?: () => void;
  leftIcon?: React.ElementType;
  rightIcon?: React.ElementType;
  isSelected?: boolean;
  isReadOnly?: boolean;
}

export interface TagGroupProps {
  children: React.ReactNode;
  spacing?: number;
  wrap?: boolean;
  className?: string;
}

const Tag: React.FC<TagProps> = ({
  children,
  variant = 'subtle',
  colorScheme = 'purple',
  size = 'md',
  borderRadius = 'full',
  onClose,
  onClick,
  leftIcon,
  rightIcon,
  isSelected,
  isReadOnly,
  ...props
}) => {
  return (
    <ChakraTag
      variant={variant}
      colorScheme={colorScheme}
      size={size}
      fontSize='xs'
      lineHeight='5'
      bg={isSelected ? 'glass-items.bg-hover' : 'glass-items.bg'}
      color={isSelected ? 'glass-items.icon-hover' : 'glass-items.icon'}
      borderRadius={borderRadius}
      border='1px solid'
      borderColor='border.light'
      pl={leftIcon ? 1.5 : 3}
      pr={3}
      _hover={{
        bg: isSelected ? 'glass-items.bg' : 'glass-items.bg-hover',
        color: isSelected ? 'glass-items.icon' : 'glass-items.icon-hover',
      }}
      transition='all 0.3s ease-out'
      onClick={onClick}
      cursor={onClick ? 'pointer' : 'default'}
      pointerEvents={isReadOnly ? 'none' : 'auto'}
      {...props}
    >
      {leftIcon && <TagLeftIcon as={leftIcon} boxSize={3.5} mr={1.5} />}
      <TagLabel fontSize='xs' lineHeight='5' fontWeight='semibold'>
        {children}
      </TagLabel>
      {rightIcon && <TagRightIcon as={rightIcon} boxSize={3.5} ml={1.5} />}
      {onClose && (
        <TagCloseButton
          boxSize='14px'
          ml={1}
          mr={-1.5}
          opacity={1}
          _hover={{
            opacity: 1,
          }}
          onClick={onClose}
        />
      )}
    </ChakraTag>
  );
};

const TagGroup: React.FC<TagGroupProps> = ({
  children,
  spacing = 2,
  wrap = true,
  className,
}) => {
  if (wrap) {
    return (
      <Wrap spacing={spacing} className={className}>
        {React.Children.map(children, (child, index) => (
          <WrapItem key={index}>{child}</WrapItem>
        ))}
      </Wrap>
    );
  }

  return (
    <HStack spacing={spacing} className={className}>
      {children}
    </HStack>
  );
};

// Preset tag variants for common use cases
export const StatusTag: React.FC<
  Omit<TagProps, 'variant' | 'colorScheme'> & {
    status: 'success' | 'warning' | 'error' | 'info';
  }
> = ({ status, children, ...props }) => {
  const statusConfig = {
    success: { colorScheme: 'green' as const, variant: 'subtle' as const },
    warning: { colorScheme: 'orange' as const, variant: 'subtle' as const },
    error: { colorScheme: 'red' as const, variant: 'subtle' as const },
    info: { colorScheme: 'blue' as const, variant: 'subtle' as const },
  };

  const config = statusConfig[status];

  return (
    <Tag {...config} {...props}>
      {children}
    </Tag>
  );
};

export const PriorityTag: React.FC<
  Omit<TagProps, 'variant' | 'colorScheme'> & {
    priority: 'high' | 'medium' | 'low';
  }
> = ({ priority, children, ...props }) => {
  const priorityConfig = {
    high: { colorScheme: 'red' as const, variant: 'solid' as const },
    medium: { colorScheme: 'orange' as const, variant: 'solid' as const },
    low: { colorScheme: 'green' as const, variant: 'solid' as const },
  };

  const config = priorityConfig[priority];

  return (
    <Tag {...config} {...props}>
      {children}
    </Tag>
  );
};

export const CategoryTag: React.FC<
  Omit<TagProps, 'variant' | 'colorScheme'> & { category: string }
> = ({ category, children, ...props }) => {
  const categoryColors = {
    urgent: { colorScheme: 'red' as const, variant: 'outline' as const },
    important: { colorScheme: 'orange' as const, variant: 'outline' as const },
    normal: { colorScheme: 'blue' as const, variant: 'outline' as const },
    low: { colorScheme: 'gray' as const, variant: 'outline' as const },
  };

  const config =
    categoryColors[category.toLowerCase() as keyof typeof categoryColors] ||
    categoryColors.normal;

  return (
    <Tag {...config} {...props}>
      {children}
    </Tag>
  );
};

export { TagGroup, Tag };
